import { Suspense, lazy, type ComponentType } from 'react'
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams } from 'react-router-dom'
import { AppLayout } from './layouts/AppLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { LoginPage } from '@/features/auth/LoginPage'
import { ProtectedRoute } from '@/features/auth/ProtectedRoute'
import { PublicRoute } from '@/features/auth/PublicRoute'
import { ModuleGate } from '@/components/auth/ModuleGate'
import { landingRoute, useWorkspaceAccess } from '@/features/auth/workspaceAccess'
import { UnauthorizedModule } from '@/features/auth/UnauthorizedModule'
import { Permissions } from '@/lib/permissions'
import { useHasPermission } from '@/hooks/useHasPermission'
// CompanyHeadGate is a permission gate that WRAPS a lazy page — it must stay
// static so the gate itself never sits behind a chunk fetch.
import { CompanyHeadGate } from '@/features/sales/CompanyHeadGate'
// NotFoundPage is the catch-all — it must render without fetching a chunk.
import { NotFoundPage } from '@/features/common/NotFoundPage'

/**
 * Wraps a lazy import with a retry that reloads the page on chunk load failure.
 * This handles the "stale chunk" problem after deployments — when the browser
 * still references old hashed filenames that no longer exist on the server.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch(() => {
      const key = 'chunk_reload_' + window.location.pathname
      const lastReload = sessionStorage.getItem(key)
      const now = Date.now()

      // Only auto-reload once per path within a 30-second window to prevent infinite loops
      if (!lastReload || now - Number(lastReload) > 30_000) {
        sessionStorage.setItem(key, String(now))
        window.location.reload()
        return new Promise<{ default: T }>(() => {})
      }

      // If we already reloaded recently, surface the error instead of looping
      return Promise.reject(new Error('Failed to load page after refresh. Please clear your browser cache and try again.'))
    })
  )
}

// Operations, Sales & Settings Pages — lazy-loaded for code splitting.
// Imported by DIRECT file path, never via a feature barrel: a barrel import
// pulls in everything it re-exports (hooks, components, types) and would blunt
// the split. Pages exported as `default` need no `.then()` remap.
const DashboardPage = lazyWithRetry(() => import('@/features/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const TaskQueuePage = lazyWithRetry(() => import('@/features/tasks/TaskQueuePage').then(m => ({ default: m.TaskQueuePage })))
const ActionItemsPage = lazyWithRetry(() => import('@/features/action-items/ActionItemsPage'))
const QuoteListPage = lazyWithRetry(() => import('@/features/quotations/QuoteListPage').then(m => ({ default: m.QuoteListPage })))
const WhatsAppInboxPage = lazyWithRetry(() => import('@/features/whatsapp/WhatsAppInboxPage').then(m => ({ default: m.WhatsAppInboxPage })))
const JobOrderListPage = lazyWithRetry(() => import('@/features/job-orders/JobOrderListPage').then(m => ({ default: m.JobOrderListPage })))
const CustomerListPage = lazyWithRetry(() => import('@/features/customers/CustomerListPage').then(m => ({ default: m.CustomerListPage })))
const NotificationLinkPage = lazyWithRetry(() => import('@/features/notifications/NotificationLinkPage'))
const SalesHomePage = lazyWithRetry(() => import('@/features/sales/SalesHomePage'))
const LeadListPage = lazyWithRetry(() => import('@/features/sales/LeadListPage'))
const LeadPoolPage = lazyWithRetry(() => import('@/features/sales/LeadPoolPage'))
const SalesDashboardPage = lazyWithRetry(() => import('@/features/sales/SalesDashboardPage'))
const BookOfBusinessPage = lazyWithRetry(() => import('@/features/sales/BookOfBusinessPage'))
const WebsiteEnquiriesReportPage = lazyWithRetry(() => import('@/features/sales/WebsiteEnquiriesReportPage'))
const SalesCustomersPage = lazyWithRetry(() => import('@/features/sales/SalesCustomersPage'))
const SalesSettingsPage = lazyWithRetry(() => import('@/features/sales/SalesSettingsPage'))
const SalesGuidePage = lazyWithRetry(() => import('@/features/sales/SalesGuidePage'))
// Sales scoreboard (P2) — leaders' Team today / full table, a rep's page, a rep's own week.
const ScoreboardTeamPage = lazyWithRetry(() => import('@/features/sales/scoreboard/TeamPage'))
const ScoreboardRepPage = lazyWithRetry(() => import('@/features/sales/scoreboard/RepPage'))
const ScoreboardMyWeekPage = lazyWithRetry(() => import('@/features/sales/scoreboard/MyWeekPage'))
// MyActivitiesPage no longer has a route: phase 11 folded the queue into SchedulePage's To-do
// view, and both /sales/follow-ups and /finance/follow-ups now redirect there. The component and
// its barrel export survive on purpose — this is the same staged retirement phase 3 used, where
// deleting a file and repointing its consumers are separate, revertible steps.
// Schedule / calendar (schedule module, phase 10). Lazy through the same wrapper as every other
// page: the grid carries drag, recurrence and a team view, and the bundle already warns above
// 500 kB on five chunks — it must not enter the shared entry chunk.
const SchedulePage = lazyWithRetry(() => import('@/features/schedule/SchedulePage'))
// Onboarding registry (customer-onboarding, phase 5). The page is a list; the weight is the
// drawer it opens — seven tabs, a composer and a PDF preview — so it lazy-loads like the rest.
const OnboardingRegistryPage = lazyWithRetry(() => import('@/features/onboarding/OnboardingRegistryPage').then(m => ({ default: m.OnboardingRegistryPage })))
// Approving one document — a full screen, not a panel inside the drawer. Lazy for the same
// reason the registry is: an approver reaches it rarely and it pulls the whole reader.
const OnboardingApprovalScreen = lazyWithRetry(() => import('@/features/onboarding/ApprovalScreen').then(m => ({ default: m.ApprovalScreen })))
// One request, read end to end. Same argument as the approval screen: a packet of up to fifty
// fields with intents, answers and flags does not fit in a drawer accordion.
const OnboardingRequestScreen = lazyWithRetry(() => import('@/features/onboarding/RequestScreen').then(m => ({ default: m.RequestScreen })))
const ServiceProviderListPage = lazyWithRetry(() => import('@/features/service-providers/ServiceProviderListPage').then(m => ({ default: m.ServiceProviderListPage })))
const CarriersListPage = lazyWithRetry(() => import('@/features/settings/carriers/CarriersListPage').then(m => ({ default: m.CarriersListPage })))
const SettingsPage = lazyWithRetry(() => import('@/features/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const QuotationReportPage = lazyWithRetry(() => import('@/features/reports/QuotationReportPage').then(m => ({ default: m.QuotationReportPage })))
const BookingTurnaroundReportPage = lazyWithRetry(() => import('@/features/reports/BookingTurnaroundReportPage').then(m => ({ default: m.BookingTurnaroundReportPage })))
const ProvisionalReportPage = lazyWithRetry(() => import('@/features/reports/provisional/ProvisionalReportPage').then(m => ({ default: m.ProvisionalReportPage })))

// Finance Module Pages — lazy-loaded for code splitting
const FinanceStartPage = lazyWithRetry(() => import('@/features/finance/pages/FinanceStartPage').then(m => ({ default: m.FinanceStartPage })))
const FinanceDashboardPage = lazyWithRetry(() => import('@/features/finance/pages/FinanceDashboardPage').then(m => ({ default: m.FinanceDashboardPage })))
const TransactionsPage = lazyWithRetry(() => import('@/features/finance/pages/TransactionsPage').then(m => ({ default: m.TransactionsPage })))
const AllocationsPage = lazyWithRetry(() => import('@/features/finance/pages/AllocationsPage').then(m => ({ default: m.AllocationsPage })))
const ChartOfAccountsPage = lazyWithRetry(() => import('@/features/finance/pages/ChartOfAccountsPage').then(m => ({ default: m.ChartOfAccountsPage })))
const SubledgerAccountsPage = lazyWithRetry(() => import('@/features/finance/pages/SubledgerAccountsPage').then(m => ({ default: m.SubledgerAccountsPage })))
const ReceivablesPage = lazyWithRetry(() => import('@/features/finance/pages/ReceivablesPage').then(m => ({ default: m.ReceivablesPage })))
const PayablesPage = lazyWithRetry(() => import('@/features/finance/pages/PayablesPage').then(m => ({ default: m.PayablesPage })))
const ChequeRegisterPage = lazyWithRetry(() => import('@/features/finance/pages/ChequeRegisterPage').then(m => ({ default: m.ChequeRegisterPage })))
const ReportsHubPage = lazyWithRetry(() => import('@/features/finance/pages/ReportsHubPage').then(m => ({ default: m.ReportsHubPage })))
const TrialBalancePage = lazyWithRetry(() => import('@/features/finance/pages/reports/TrialBalancePage').then(m => ({ default: m.TrialBalancePage })))
const BalanceSheetPage = lazyWithRetry(() => import('@/features/finance/pages/reports/BalanceSheetPage').then(m => ({ default: m.BalanceSheetPage })))
const ProfitLossPage = lazyWithRetry(() => import('@/features/finance/pages/reports/ProfitLossPage').then(m => ({ default: m.ProfitLossPage })))
const GeneralLedgerPage = lazyWithRetry(() => import('@/features/finance/pages/reports/GeneralLedgerPage').then(m => ({ default: m.GeneralLedgerPage })))
const AgingReportPage = lazyWithRetry(() => import('@/features/finance/pages/reports/AgingReportPage').then(m => ({ default: m.AgingReportPage })))
const StatementPage = lazyWithRetry(() => import('@/features/finance/pages/reports/StatementPage').then(m => ({ default: m.StatementPage })))
const CashPositionPage = lazyWithRetry(() => import('@/features/finance/pages/reports/CashPositionPage').then(m => ({ default: m.CashPositionPage })))
const VATReportPage = lazyWithRetry(() => import('@/features/finance/pages/reports/VATReportPage').then(m => ({ default: m.VATReportPage })))
const FinanceSettingsPage = lazyWithRetry(() => import('@/features/finance/pages/FinanceSettingsPage').then(m => ({ default: m.FinanceSettingsPage })))
const VoucherTypesPage = lazyWithRetry(() => import('@/features/finance/pages/settings/VoucherTypesPage').then(m => ({ default: m.VoucherTypesPage })))
const AccountMappingPage = lazyWithRetry(() => import('@/features/finance/pages/settings/AccountMappingPage').then(m => ({ default: m.AccountMappingPage })))
const CurrenciesPage = lazyWithRetry(() => import('@/features/finance/pages/settings/CurrenciesPage').then(m => ({ default: m.CurrenciesPage })))
const FiscalPeriodsPage = lazyWithRetry(() => import('@/features/finance/pages/settings/FiscalPeriodsPage').then(m => ({ default: m.FiscalPeriodsPage })))
const AuditTrailPage = lazyWithRetry(() => import('@/features/finance/pages/settings/AuditTrailPage').then(m => ({ default: m.AuditTrailPage })))
const SoaEmailConfigPage = lazyWithRetry(() => import('@/features/finance/pages/settings/SoaEmailConfigPage').then(m => ({ default: m.SoaEmailConfigPage })))
const GuidePage = lazyWithRetry(() => import('@/features/finance/pages/GuidePage').then(m => ({ default: m.GuidePage })))
const InvoicesPage = lazyWithRetry(() => import('@/features/finance/pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })))

/**
 * Gates Finance routes behind finance:access — renders UnauthorizedModule in-place if missing.
 *
 * Per RBAC Module spec #10 §2.3, generalized via [ModuleGate] in §3.2.
 * This thin shim preserves the original FinanceGate name for in-file readability.
 */
function FinanceGate({ children }: { children: React.ReactNode }) {
  return (
    <ModuleGate permission={Permissions.FINANCE_ACCESS} moduleName="Finance">
      {children}
    </ModuleGate>
  )
}

/**
 * `/job-orders/:jobOrderId` → `/job-orders?selected=:jobOrderId` (T2-D14).
 *
 * The job order drawer is a query-param surface (`?selected=`), so the path form — the one
 * that matches how a job order id is written everywhere else — matched no route and landed on
 * the catch-all. Redirecting is strictly better than the 404: the id in the URL is real, the
 * list page knows how to open it, and an unknown id degrades to the list with an empty drawer
 * rather than to a dead end.
 *
 * `replace` keeps the un-routable path out of history, so Back does not bounce through it.
 */
function JobOrderDeepLinkRedirect() {
  const { jobOrderId } = useParams<{ jobOrderId: string }>()
  const { search } = useLocation()
  if (!jobOrderId) return <Navigate replace to="/job-orders" />
  const params = new URLSearchParams(search)
  params.set('selected', jobOrderId)
  return <Navigate replace to={`/job-orders?${params.toString()}`} />
}

/**
 * `/settings/email-accounts` → `/settings?tab=email-accounts`.
 *
 * The Gmail OAuth callback in ai-py returns the browser to `/settings/email-accounts` with
 * `success`/`email` or `error` appended, but Settings picks its tab from `?tab=`, so that path
 * matched no route and a freshly connected mailbox ended on the 404 page. The callback's
 * params are kept on the redirect.
 */
function EmailAccountsOAuthReturn() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', 'email-accounts')
  return <Navigate replace to={`/settings?${params.toString()}`} />
}

/** Preserve existing bookmarks while keeping Job Orders as the only user-facing module. */
function WarehouseJobsRedirect() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('type', 'WAREHOUSING')
  params.delete('category')
  return <Navigate replace to={`/job-orders?${params.toString()}`} />
}

/**
 * Suspense wrapper with skeleton loading for lazy-loaded operations/sales/settings
 * pages. Sibling of [FinanceSuspense] with the same fallback — kept separate so the
 * Finance implementation stays untouched.
 */
/**
 * Sales → Companies is a customer list too. Without `customer-list:view` it behaves like the
 * Operations Customers page does: one linked company on its own, otherwise no access
 * (see features/customers/customerListAccess).
 */
function SalesCompaniesRoute() {
  const canSeeList = useHasPermission(Permissions.CUSTOMER_LIST_VIEW)
  return canSeeList
    ? <SalesCustomersPage />
    : <CustomerListPage activityModule="SALES" homePath="/sales/home" />
}

function OperationsSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="space-y-6 p-1">
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
          <div className="h-4 w-64 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
            ))}
          </div>
          <div className="h-[400px] animate-pulse rounded-lg border bg-muted" />
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

/** Suspense wrapper with skeleton loading for lazy-loaded finance pages */
function FinanceSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="space-y-6 p-1">
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
          <div className="h-4 w-64 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
            ))}
          </div>
          <div className="h-[400px] animate-pulse rounded-lg border bg-muted" />
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

/**
 * The index route. Operations users get the Operations dashboard; a user without Operations —
 * Sales only, Finance only, an onboarding approver — is redirected to their own workspace
 * instead of an Operations home they cannot use (workspaceAccess.landingRoute).
 */
function HomeRoute() {
  const access = useWorkspaceAccess()
  const target = landingRoute(access)
  if (target !== '/') return <Navigate replace to={target} />
  // No workspace at all: say so, rather than draw an Operations dashboard they cannot use. The
  // back link would point here again, so it is hidden.
  if (!access.operations) {
    return (
      <UnauthorizedModule
        moduleName="FreighAI"
        helperText="Your role does not give access to Operations, Sales or Finance yet. Ask your administrator for access."
        showBackLink={false}
      />
    )
  }
  return <OperationsSuspense><DashboardPage /></OperationsSuspense>
}

/**
 * Router configuration with protected and public routes
 * Per DEMO-ROADMAP.md Milestone 2.3:
 * - Protected routes require authentication
 * - Public routes (login) redirect authenticated users to dashboard
 * - Preserves intended destination for post-login redirect
 */
export const router = createBrowserRouter([
  // Public routes (login, forgot password, etc.)
  {
    path: '/login',
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    children: [
      { index: true, element: <LoginPage /> },
    ],
  },
  // Protected routes (require authentication)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      // `/` is the Operations dashboard for those who may enter Operations; anyone else is sent to
      // the first workspace they may enter (HomeRoute) — after login too, which lands on `/`.
      { index: true, element: <HomeRoute /> },
      { path: 'tasks', element: <OperationsSuspense><TaskQueuePage /></OperationsSuspense> },
      // Action Items — internal team task management. Mounted per workspace (like
      // My Follow-ups) because AppLayout derives the workspace from the URL prefix:
      // a single /action-items mount would snap the sidebar back to Operations.
      // /action-items is the canonical mount (deep links); ungated like /tasks —
      // visibility is record-scoped by the backend (action-item:view).
      { path: 'action-items', element: <OperationsSuspense><ActionItemsPage /></OperationsSuspense> },
      { path: 'quotations', element: <OperationsSuspense><QuoteListPage /></OperationsSuspense> },
      // WhatsApp Inbox (Phase 3) — every connected number's conversations, two-pane.
      {
        path: 'whatsapp',
        element: (
          <ModuleGate permission={Permissions.WHATSAPP_CONVERSATION_VIEW} moduleName="WhatsApp">
            <OperationsSuspense><WhatsAppInboxPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      { path: 'job-orders', element: <OperationsSuspense><JobOrderListPage /></OperationsSuspense> },
      // T2-D13/T2-D14: `/job-orders/JO-2026-00086` is the URL everybody guesses — it is what
      // the id looks like in every email, log line and screenshot — but the drawer is opened
      // by a query param, so the path form fell through to the catch-all. Forward it to the
      // canonical shape rather than 404-ing a link that names a real job order.
      { path: 'job-orders/:jobOrderId', element: <JobOrderDeepLinkRedirect /> },
      {
        path: 'warehouse-jobs',
        element: <WarehouseJobsRedirect />,
      },
      { path: 'customers', element: <OperationsSuspense><CustomerListPage /></OperationsSuspense> },
      // Notification link resolver. Email and WhatsApp copies point here rather than at a
      // workspace route, because one message body reaches recipients with different access —
      // this forwards each of them to somewhere they can actually open. Deliberately ungated.
      { path: 'n/:notificationId', element: <OperationsSuspense><NotificationLinkPage /></OperationsSuspense> },

      // Sales workspace · v1 Lead Management
      {
        path: 'sales/home',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesHomePage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Sales scoreboard (P2). Each page asks the server and goes back to My day when the
      // scoreboard is off for the tenant (404 SCOREBOARD_DISABLED) or the reader may not see it,
      // so a tenant without it never reaches anything new.
      {
        path: 'sales/team',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><ScoreboardTeamPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/team/:userId',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><ScoreboardRepPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/my-week',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><ScoreboardMyWeekPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/leads',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><LeadListPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Shared pool of unowned opportunities (Kiko Leads #3). Its own route rather than a
      // Pipeline filter — the Pipeline is scoped to what YOU own; this is everyone's.
      // The Sales user guide. Two routes: the landing, and one per section.
      // Topics deep-link by anchor within a section rather than by route, so a
      // link into the guide never breaks when a topic is reworded.
      {
        path: 'sales/guide',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesGuidePage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/guide/:section',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesGuidePage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/pool',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><LeadPoolPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'sales/dashboard',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesDashboardPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        // Website enquiries — the monthly report (Kiko #29). Its endpoints are gated on `lead:view`.
        path: 'sales/website-enquiries',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <ModuleGate permission={Permissions.LEAD_VIEW} moduleName="Website enquiries">
              <OperationsSuspense><WebsiteEnquiriesReportPage /></OperationsSuspense>
            </ModuleGate>
          </ModuleGate>
        ),
      },
      {
        // Book of business — paying-accounts revenue, restricted to the company head. It also needs
        // `report:accounts-revenue`, the gateway row for its one endpoint (audit S-82): without it the
        // page would load and then 403 on every call once enforcement is raised. The company head
        // without the grant sees the access-required state, never an empty report.
        path: 'sales/book-of-business',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <CompanyHeadGate>
              <ModuleGate permission={Permissions.REPORT_ACCOUNTS_REVENUE} moduleName="Book of business">
                <OperationsSuspense><BookOfBusinessPage /></OperationsSuspense>
              </ModuleGate>
            </CompanyHeadGate>
          </ModuleGate>
        ),
      },
      // Customers under Sales · Sales-owned page (lifecycle-stage segmentation).
      // Reuses the master-data customer hook/components read-only; the Operations
      // CustomerListPage stays untouched.
      {
        path: 'sales/customers',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesCompaniesRoute /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Sales-scoped settings — keeps the rep inside the Sales workspace.
      // Gated on SALES_ACCESS: the Sales module is the minimum to open Sales Settings. Inside, My
      // email is for every sales user and the rest needs SALES_SETTINGS_EDIT (see SalesSettingsPage).
      {
        path: 'sales/settings',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><SalesSettingsPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // The old follow-ups route, kept as a REDIRECT (phase 11, done-when 5). This is the last
      // step of the retirement: the vocabulary is gone from the product, and the path survives
      // only so existing bookmarks, saved links and notification rows still land somewhere real.
      // `?view=todo` opens the queue, which is what this page always was.
      { path: 'sales/follow-ups', element: <Navigate replace to="/sales/schedule?view=todo" /> },
      // Schedule — sales mount. ONE page, three mounts (sales / finance / operations), exactly
      // as MyActivitiesPage was: the Calendar|To-do toggle is a view of one projection,
      // never two routes. Gated on calendar-event:view — the nav entry is hidden without it.
      {
        path: 'sales/schedule',
        element: (
          <ModuleGate permission={Permissions.CALENDAR_EVENT_VIEW} moduleName="Calendar">
            <OperationsSuspense><SchedulePage defaultModule="SALES" /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Onboarding — the case registry. Mounted under /sales so AppLayout keeps the Sales
      // sidebar (it derives the workspace from the URL prefix), and mounted ONCE: if the
      // registry ever needs reaching from the customer record, follow the action-items
      // precedent above and mount it twice rather than moving this.
      //
      // Gated on ONBOARDING_CASE_VIEW, not SALES_ACCESS — following sales/schedule. An
      // approver (typically a Finance Officer) holds onboarding-case:view and
      // onboarding-case:approve and may well hold no sales:access at all; gating on
      // SALES_ACCESS would lock them out of the only page the "Waiting on you" filter is
      // reachable from, which is their whole route to the work.
      {
        path: 'sales/onboarding',
        element: (
          <ModuleGate permission={Permissions.ONBOARDING_CASE_VIEW} moduleName="Onboarding">
            <OperationsSuspense><OnboardingRegistryPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Approving one document of one case. A CHILD path of the registry rather than a
      // sibling, so the breadcrumb, the sidebar and "back to the case" all agree about
      // where the reader is.
      //
      // Gated on ONBOARDING_CASE_APPROVE — a stronger gate than the registry's, because
      // this page's whole purpose is the act. Someone with :view and not :approve reaches
      // the document read-only through the drawer, which is the honest place for them.
      {
        path: 'sales/onboarding/:caseId/approve/:templateId',
        element: (
          <ModuleGate permission={Permissions.ONBOARDING_CASE_APPROVE} moduleName="Onboarding">
            <OperationsSuspense><OnboardingApprovalScreen /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // One request — what we asked, and what came back.
      //
      // Gated on :VIEW, not :request. This screen writes nothing: it reads a snapshot that is
      // already frozen, and every act it might tempt you into — sending, cancelling, confirming
      // what the customer answered — stays on the Requests tab behind the stronger gate. Anyone
      // who may look at the case may read what was sent from it.
      {
        path: 'sales/onboarding/:caseId/requests/:requestId',
        element: (
          <ModuleGate permission={Permissions.ONBOARDING_CASE_VIEW} moduleName="Onboarding">
            <OperationsSuspense><OnboardingRequestScreen /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Action Items — sales mount (keeps the Sales sidebar active).
      {
        path: 'sales/action-items',
        element: (
          <ModuleGate permission={Permissions.SALES_ACCESS} moduleName="Sales">
            <OperationsSuspense><ActionItemsPage defaultModule="SALES" /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Schedule — operations mount, the workspace-neutral one.
      {
        path: 'schedule',
        element: (
          <ModuleGate permission={Permissions.CALENDAR_EVENT_VIEW} moduleName="Calendar">
            <OperationsSuspense><SchedulePage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      { path: 'service-providers', element: <OperationsSuspense><ServiceProviderListPage /></OperationsSuspense> },
      { path: 'carriers', element: <OperationsSuspense><CarriersListPage /></OperationsSuspense> },
      {
        path: 'settings',
        element: (
          <ModuleGate permission={Permissions.SETTINGS_ACCESS} moduleName="Settings">
            <OperationsSuspense><SettingsPage /></OperationsSuspense>
          </ModuleGate>
        )
      },
      { path: 'settings/email-accounts', element: <EmailAccountsOAuthReturn /> },

      // Reports (Operations workspace)
      // Quotation Report and Booking Turnaround are gated on their own report permissions, the
      // ones their data endpoints already carry — ungated until Oct 2026, when Infinity took them
      // away from the pricing and operations teams.
      {
        path: 'reports/quotations',
        element: (
          <ModuleGate permission={Permissions.REPORT_QUOTATION} moduleName="Quotation Report">
            <OperationsSuspense><QuotationReportPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      {
        path: 'reports/booking-turnaround',
        element: (
          <ModuleGate permission={Permissions.REPORT_BOOKING_TURNAROUND} moduleName="Booking Turnaround">
            <OperationsSuspense><BookingTurnaroundReportPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },
      // Provisional Report (DECISIONS.md D3): gated first of the three - it is the first
      // surface showing company-wide realised profit. Same permission as the gateway row and the
      // nav entry. Existing tenants get it only through the ADMIN grant script; a tenant
      // provisioned after this release has it on ADMIN from the role seed (D25). This gate only
      // hides the page: the report is closed by job-order-service's own report:provisional check
      // on both endpoints (audit-2 S-05), since the gateway row only logs under SHADOW.
      {
        path: 'reports/provisional',
        element: (
          <ModuleGate permission={Permissions.REPORT_PROVISIONAL} moduleName="Provisional Report">
            <OperationsSuspense><ProvisionalReportPage /></OperationsSuspense>
          </ModuleGate>
        ),
      },

      // Finance Module Routes — lazy-loaded for code splitting
      { path: 'finance', element: <FinanceGate><FinanceSuspense><FinanceStartPage /></FinanceSuspense></FinanceGate> },
      // Unified My Follow-ups panel (5th surface) — finance mount (defaults to the finance filter).
      { path: 'finance/follow-ups', element: <Navigate replace to="/finance/schedule?view=todo" /> },
      // Schedule — finance mount.
      // Gated on CALENDAR_EVENT_VIEW like its two sibling mounts. Without it the page rendered
      // for anyone with finance access and then sat permanently empty, because every call it
      // makes is gated on that permission at the gateway — an honest "no access" beats a screen
      // that loads and never fills.
      {
        path: 'finance/schedule',
        element: (
          <FinanceGate>
            <ModuleGate permission={Permissions.CALENDAR_EVENT_VIEW} moduleName="Calendar">
              <FinanceSuspense><SchedulePage defaultModule="FINANCE" /></FinanceSuspense>
            </ModuleGate>
          </FinanceGate>
        ),
      },
      // Action Items — finance mount (keeps the Finance work-queues sidebar active).
      { path: 'finance/action-items', element: <FinanceGate><FinanceSuspense><ActionItemsPage defaultModule="FINANCE" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/dashboard', element: <FinanceGate><FinanceSuspense><FinanceDashboardPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/transactions', element: <FinanceGate><FinanceSuspense><TransactionsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/invoices', element: <FinanceGate><FinanceSuspense><InvoicesPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/allocations', element: <FinanceGate><FinanceSuspense><AllocationsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/receivables', element: <FinanceGate><FinanceSuspense><ReceivablesPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/payables', element: <FinanceGate><FinanceSuspense><PayablesPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/cheques', element: <FinanceGate><FinanceSuspense><ChequeRegisterPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/masters/accounts', element: <FinanceGate><FinanceSuspense><ChartOfAccountsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/masters/parties', element: <FinanceGate><FinanceSuspense><SubledgerAccountsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports', element: <FinanceGate><FinanceSuspense><ReportsHubPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/trial-balance', element: <FinanceGate><FinanceSuspense><TrialBalancePage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/balance-sheet', element: <FinanceGate><FinanceSuspense><BalanceSheetPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/profit-loss', element: <FinanceGate><FinanceSuspense><ProfitLossPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/general-ledger', element: <FinanceGate><FinanceSuspense><GeneralLedgerPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/aging/receivables', element: <FinanceGate><FinanceSuspense><AgingReportPage type="RECEIVABLE" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/aging/payables', element: <FinanceGate><FinanceSuspense><AgingReportPage type="PAYABLE" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/statement/customer', element: <FinanceGate><FinanceSuspense><StatementPage variant="customer" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/statement/customer/:id', element: <FinanceGate><FinanceSuspense><StatementPage variant="customer" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/statement/vendor', element: <FinanceGate><FinanceSuspense><StatementPage variant="vendor" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/statement/vendor/:id', element: <FinanceGate><FinanceSuspense><StatementPage variant="vendor" /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/cash-position', element: <FinanceGate><FinanceSuspense><CashPositionPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/reports/vat', element: <FinanceGate><FinanceSuspense><VATReportPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings', element: <FinanceGate><FinanceSuspense><FinanceSettingsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/voucher-types', element: <FinanceGate><FinanceSuspense><VoucherTypesPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/account-mapping', element: <FinanceGate><FinanceSuspense><AccountMappingPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/currencies', element: <FinanceGate><FinanceSuspense><CurrenciesPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/periods', element: <FinanceGate><FinanceSuspense><FiscalPeriodsPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/audit-log', element: <FinanceGate><FinanceSuspense><AuditTrailPage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/settings/soa-email-config', element: <FinanceGate><FinanceSuspense><SoaEmailConfigPage /></FinanceSuspense></FinanceGate> },

      // Finance User Guide
      { path: 'finance/guide', element: <FinanceGate><FinanceSuspense><GuidePage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/guide/:section', element: <FinanceGate><FinanceSuspense><GuidePage /></FinanceSuspense></FinanceGate> },
      { path: 'finance/guide/:section/:topic', element: <FinanceGate><FinanceSuspense><GuidePage /></FinanceSuspense></FinanceGate> },
    ],
  },
  // 404 page (accessible to all)
  {
    path: '*',
    element: <NotFoundPage />,
  },
])

export function Router() {
  return <RouterProvider router={router} />
}
